import {
  CheckCircleIcon,
  ClockIcon,
  ListBulletIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { BsKanban, BsMicrosoftTeams } from 'react-icons/bs';
import { FaStickyNote } from 'react-icons/fa';
import { GiTomato } from 'react-icons/gi';
import { SiGooglecalendar } from 'react-icons/si';
import { Link, Navigate } from 'react-router-dom';

import { LanguageSelector } from '@/common/components/LanguageSelector';
import Modal from '@/common/components/Modal';
import { useAuthStore } from '@/common/stores/useAuthStore';
import { AVAILABLE_WIDGETS } from '@/common/utils/constants';
import Login from '@/pages/Login';

const floatingCardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: 0.3 + i * 0.15, duration: 0.6, ease: 'easeOut' as const },
  }),
};

const NotesCard = () => (
  <motion.div
    className="bg-amber-50 border border-amber-200 rounded-2xl p-5 shadow-lg w-52 -rotate-3"
    custom={0}
    initial="hidden"
    variants={floatingCardVariants}
    viewport={{ once: true }}
    whileInView="visible"
  >
    <div className="flex items-center gap-2 mb-3">
      <FaStickyNote className="w-4 h-4 text-amber-600" />
      <span className="text-sm font-semibold text-amber-900">Quick Note</span>
    </div>
    <p className="text-xs text-amber-800 leading-relaxed">
      Take notes to keep track of crucial details, and accomplish more tasks with ease.
    </p>
    <div className="mt-3 flex items-center gap-1">
      <CheckCircleIcon className="w-4 h-4 text-amber-500" />
      <span className="text-[10px] text-amber-600">Auto-saved</span>
    </div>
  </motion.div>
);

const PomodoroCard = () => (
  <motion.div
    className="bg-white border border-gray-200 rounded-2xl p-5 shadow-lg w-48 rotate-2"
    custom={1}
    initial="hidden"
    variants={floatingCardVariants}
    viewport={{ once: true }}
    whileInView="visible"
  >
    <div className="flex items-center gap-2 mb-3">
      <ClockIcon className="w-4 h-4 text-gray-600" />
      <span className="text-sm font-semibold text-gray-900">Focus Timer</span>
    </div>
    <div className="flex items-center justify-center py-2">
      <span className="text-3xl font-bold text-gray-900 tabular-nums">25:00</span>
    </div>
    <div className="flex items-center justify-center gap-2 mt-1">
      <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">
        Focus
      </span>
      <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Break</span>
    </div>
  </motion.div>
);

const ChecklistCard = () => (
  <motion.div
    className="bg-white border border-gray-200 rounded-2xl p-5 shadow-lg w-56 -rotate-1"
    custom={2}
    initial="hidden"
    variants={floatingCardVariants}
    viewport={{ once: true }}
    whileInView="visible"
  >
    <div className="flex items-center gap-2 mb-3">
      <ListBulletIcon className="w-4 h-4 text-blue-600" />
      <span className="text-sm font-semibold text-gray-900">Today's tasks</span>
    </div>
    <div className="space-y-2">
      <label className="flex items-center gap-2">
        <input
          className="rounded text-blue-600 w-3.5 h-3.5 pointer-events-none"
          type="checkbox"
          checked
          readOnly
        />
        <span className="text-xs text-gray-500 line-through">Review campaign</span>
        <span className="ml-auto text-[10px] text-blue-600 font-medium">60%</span>
      </label>
      <label className="flex items-center gap-2">
        <input
          className="rounded text-blue-600 w-3.5 h-3.5 pointer-events-none"
          type="checkbox"
          readOnly
        />
        <span className="text-xs text-gray-700">Design PPT #4</span>
        <span className="ml-auto text-[10px] text-red-500 font-medium">112%</span>
      </label>
      <label className="flex items-center gap-2">
        <input
          className="rounded text-blue-600 w-3.5 h-3.5 pointer-events-none"
          type="checkbox"
          readOnly
        />
        <span className="text-xs text-gray-700">Write blog post</span>
      </label>
    </div>
  </motion.div>
);

const IntegrationsCard = () => (
  <motion.div
    className="bg-white border border-gray-200 rounded-2xl p-5 shadow-lg w-52 rotate-3"
    custom={3}
    initial="hidden"
    variants={floatingCardVariants}
    viewport={{ once: true }}
    whileInView="visible"
  >
    <span className="text-sm font-semibold text-gray-900">Integrations</span>
    <div className="flex items-center gap-3 mt-4">
      <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
        <SiGooglecalendar className="w-5 h-5 text-red-500" />
      </div>
      <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
        <BsMicrosoftTeams className="w-5 h-5 text-purple-600" />
      </div>
      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
        <SparklesIcon className="w-5 h-5 text-blue-500" />
      </div>
    </div>
  </motion.div>
);

const Home = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Dont Tab — Your Productivity Dashboard in a New Tab</title>
        <meta
          content="Dont Tab is your personal productivity dashboard with notes, calendars, task boards, Pomodoro timer, and more — right where you work."
          name="description"
        />
        <link href="https://donttab.com/" rel="canonical" />
        <meta content="https://donttab.com/" property="og:url" />
      </Helmet>

      {/* Navbar */}
      <nav className="fixed w-full bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-8">
              <button
                className="cursor-pointer"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <img alt="Dont Tab Logo" className="h-10 w-auto" src="/logo.png" />
              </button>
              <button
                className="hidden sm:inline-block text-sm text-gray-600 hover:text-gray-900 transition-colors"
                onClick={() =>
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                {t('home.features')}
              </button>
              <button
                className="hidden sm:inline-block text-sm text-gray-600 hover:text-gray-900 transition-colors"
                onClick={() =>
                  document.getElementById('widgets')?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                {t('home.availableWidgets')}
              </button>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <button
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors px-3 py-2"
                onClick={() => setIsLoginModalOpen(true)}
              >
                {t('home.login')}
              </button>
              <button
                className="bg-gray-900 text-white text-sm px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                onClick={() => setIsLoginModalOpen(true)}
              >
                {t('home.getStarted')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section
          className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden"
          style={{
            backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="hidden lg:block">
              <div className="absolute -left-4 top-8 xl:left-0">
                <NotesCard />
              </div>
              <div className="absolute -right-4 top-4 xl:right-0">
                <PomodoroCard />
              </div>
              <div className="absolute left-0 bottom-0 xl:left-8">
                <ChecklistCard />
              </div>
              <div className="absolute right-0 bottom-4 xl:right-8">
                <IntegrationsCard />
              </div>
            </div>

            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 mb-8 px-4 py-2 bg-white rounded-xl shadow-sm border border-gray-100"
                initial={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5 }}
              >
                <img alt="Dont Tab Logo" className="h-8 w-8" src="/logo.png" />
              </motion.div>

              <motion.h1
                animate={{ opacity: 1, y: 0 }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <span className="text-gray-900">{t('home.titleLine1')}</span>
                <br />
                <span className="text-gray-400">{t('home.titleLine2')}</span>
              </motion.h1>

              <motion.p
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 text-lg md:text-xl text-gray-500 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {t('home.subtitle')}
              </motion.p>

              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="mt-10"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <button
                  className="bg-gray-900 cursor-pointer text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-800 transition-all hover:shadow-lg hover:shadow-gray-900/20"
                  onClick={() => setIsLoginModalOpen(true)}
                >
                  {t('home.getStarted')}
                </button>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="lg:hidden pb-12 -mt-8">
          <div className="flex gap-4 overflow-x-auto px-4 pb-4 snap-x snap-mandatory scrollbar-hide">
            <div className="snap-center shrink-0">
              <NotesCard />
            </div>
            <div className="snap-center shrink-0">
              <PomodoroCard />
            </div>
            <div className="snap-center shrink-0">
              <ChecklistCard />
            </div>
            <div className="snap-center shrink-0">
              <IntegrationsCard />
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 md:py-28" id="features">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              <motion.div
                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <BsKanban className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex gap-1">
                    <div className="w-6 h-6 bg-amber-100 rounded-md flex items-center justify-center">
                      <FaStickyNote className="w-3 h-3 text-amber-600" />
                    </div>
                    <div className="w-6 h-6 bg-green-100 rounded-md flex items-center justify-center">
                      <ListBulletIcon className="w-3 h-3 text-green-600" />
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('home.feature1Title')}
                </h3>
                <p className="text-gray-500 leading-relaxed">{t('home.feature1Desc')}</p>
              </motion.div>

              <motion.div
                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                    <GiTomato className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex gap-1">
                    <div className="w-6 h-6 bg-purple-100 rounded-md flex items-center justify-center">
                      <SparklesIcon className="w-3 h-3 text-purple-600" />
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('home.feature2Title')}
                </h3>
                <p className="text-gray-500 leading-relaxed">{t('home.feature2Desc')}</p>
              </motion.div>

              <motion.div
                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                    <SiGooglecalendar className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                    <BsMicrosoftTeams className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {t('home.feature3Title')}
                </h3>
                <p className="text-gray-500 leading-relaxed">{t('home.feature3Desc')}</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Widgets Showcase */}
        <section className="py-20 md:py-28 bg-white" id="widgets">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <motion.h2
                className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                {t('home.availableWidgets')}
              </motion.h2>
              <motion.p
                className="text-lg text-gray-500 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                {t('home.availableWidgetsDesc')}
              </motion.p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {AVAILABLE_WIDGETS.map((widget, index) => (
                <motion.div
                  key={widget.id}
                  className="group relative bg-gray-50 hover:bg-gray-100/80 rounded-2xl p-6 transition-all hover:shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  whileInView={{ opacity: 1, y: 0 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
                      <widget.icon className="w-5 h-5 text-gray-700" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-base font-semibold text-gray-900">
                          {t(`widgets.${widget.id}.title`)}
                        </h3>
                        {widget.premium ? (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 bg-linear-to-r from-amber-400 to-orange-400 text-white rounded-full">
                            {t('home.premium')}
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">
                            {t('home.free')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        {t(`widgets.${widget.id}.description`)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="text-center mt-14"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <button
                className="bg-gray-900 cursor-pointer text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-800 transition-all hover:shadow-lg hover:shadow-gray-900/20"
                onClick={() => setIsLoginModalOpen(true)}
              >
                {t('home.startForFree')}
              </button>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <img alt="Dont Tab Logo" className="h-8 w-auto" src="/logo.png" />
            <div className="flex space-x-6">
              <Link
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                to="/privacy"
              >
                {t('home.privacyPolicy')}
              </Link>
              <Link
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                to="/terms"
              >
                {t('home.termsOfUse')}
              </Link>
            </div>
          </div>
        </div>
      </footer>

      <Modal isOpen={isLoginModalOpen} title="" onClose={() => setIsLoginModalOpen(false)}>
        <Login />
      </Modal>
    </div>
  );
};

export default Home;
